import { getCurrentInstance } from 'vue'

export function useFileAttachments(attachmentsRef, emit, options = {}) {
    const MAX_TOTAL_SIZE = options.maxTotalSize || 3 * 1024 * 1024 // 3MB
    const getAdditionalSize = options.getAdditionalSize || (() => 0)
    const instance = getCurrentInstance()
    const errorHandler = instance?.proxy?.$errorHandler || ((msg) => console.error(msg))

    function formatFileSize(bytes) {
        if (bytes === 0) {
            return '0 Bytes'
        }

        const k = 1024
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        const size = parseFloat((bytes / Math.pow(k, i)).toFixed(2))
        return size + ' ' + sizes[i]
    }

    function getFileIcon(file) {
        const type = file.content_type?.toLowerCase() || ''

        if (type.startsWith('image/')) {
            return 'image'
        }

        if (type.includes('pdf')) {
            return 'file-pdf'
        }

        if (type.includes('word') || type.includes('doc')) {
            return 'file-word'
        }

        if (type.includes('text')) {
            return 'file-text'
        }

        return 'file'
    }

    function getFileSize(file) {
        if (file.content) {
            return file.content.length * 0.75
        }

        return file.size || 0
    }

    function getAttachmentCollectionSize(attachments = attachmentsRef.value) {
        return attachments.reduce((total, file) => total + getFileSize(file), 0)
    }

    function getTotalAttachmentSize(attachments = attachmentsRef.value) {
        return getAttachmentCollectionSize(attachments) + (Number(getAdditionalSize()) || 0)
    }

    async function processFiles(files) {
        const newAttachments = [...attachmentsRef.value]
        const limitMessage = `Email uploads can be up to ${formatFileSize(
            MAX_TOTAL_SIZE,
        )} total. Remove an attachment or image, or choose a smaller file.`

        for (const file of files) {
            const currentTotal = getTotalAttachmentSize(newAttachments)

            if (currentTotal >= MAX_TOTAL_SIZE) {
                errorHandler(limitMessage)
                break
            }

            let processedFile = file

            if (file.type.startsWith('image/')) {
                const wouldExceedLimit = file.size > 1024 * 1024 || currentTotal + file.size > MAX_TOTAL_SIZE

                if (wouldExceedLimit) {
                    try {
                        processedFile = await compressImage(file, MAX_TOTAL_SIZE - currentTotal)

                        if (!processedFile) {
                            errorHandler(`Image "${file.name}" is too large. ${limitMessage}`)
                            continue
                        }
                    } catch (err) {
                        console.error('Image compression failed:', err)
                        errorHandler(`Failed to compress "${file.name}".`)
                        continue
                    }
                }
            } else {
                const wouldExceedLimit = currentTotal + file.size > MAX_TOTAL_SIZE

                if (wouldExceedLimit) {
                    errorHandler(`File "${file.name}" would exceed the ${formatFileSize(MAX_TOTAL_SIZE)} email upload limit.`)
                    continue
                }
            }

            try {
                const base64 = await fileToBase64(processedFile)
                const estimated = base64.length * 0.75
                const totalAfterAdd = currentTotal + estimated

                if (totalAfterAdd > MAX_TOTAL_SIZE) {
                    errorHandler(`File "${file.name}" would exceed the ${formatFileSize(MAX_TOTAL_SIZE)} email upload limit.`)
                    continue
                }

                newAttachments.push({
                    filename: processedFile.name,
                    content_type: processedFile.type,
                    content: base64,
                    size: processedFile.size,
                    original_size: file.size,
                })
            } catch (err) {
                errorHandler(`Failed to process file "${file.name}"`)
            }
        }

        attachmentsRef.value = newAttachments

        if (emit) {
            emit('update:attachments', newAttachments)
        }
    }

    function fileToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)

            reader.onload = () => {
                const base64 = reader.result.split(',')[1]
                resolve(base64)
            }

            reader.onerror = (error) => {
                reject(error)
            }
        })
    }

    function compressImage(file, maxSize) {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            const img = new Image()

            img.onload = () => {
                let width = img.width
                let height = img.height
                const maxDim = 1920

                const exceedsMaxDimension = width > maxDim || height > maxDim

                if (exceedsMaxDimension) {
                    if (width > height) {
                        height = (height * maxDim) / width
                        width = maxDim
                    } else {
                        width = (width * maxDim) / height
                        height = maxDim
                    }
                }

                canvas.width = width
                canvas.height = height
                ctx.drawImage(img, 0, 0, width, height)

                const tryQuality = (quality) => {
                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                reject(new Error('Compression failed'))
                                return
                            }

                            const sizeWithinLimit = blob.size <= maxSize
                            const tooLowQuality = quality <= 0.1

                            if (sizeWithinLimit || tooLowQuality) {
                                const compressed = new File([blob], file.name, {
                                    type: file.type,
                                    lastModified: Date.now(),
                                })

                                if (sizeWithinLimit) {
                                    resolve(compressed)
                                } else {
                                    resolve(null)
                                }
                            } else {
                                tryQuality(quality - 0.1)
                            }
                        },
                        file.type,
                        quality,
                    )
                }

                tryQuality(0.8)
            }

            img.onerror = () => {
                reject(new Error('Image load failed'))
            }

            img.src = URL.createObjectURL(file)
        })
    }

    return {
        processFiles,
        getFileIcon,
        formatFileSize,
        getTotalAttachmentSize,
    }
}
